import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { MdLock, MdOpenInNew } from 'react-icons/md';
import { BsCash, BsCreditCard2Back } from 'react-icons/bs';
import { BiLinkExternal } from 'react-icons/bi';
import MainButton from '../../../components/buttons/main';
import { useCurrency } from '../../../hooks/useCurrency';
import StripeFullPaymentContainer from './integrations/stripeFullPaymentContainer';
import PayntPaymentContainer from './integrations/payntPaymentContainer';
import ZenPaymentContainer from './integrations/zenPaymentContainer';
import { CheckBox } from '../../../components/inputs/checkBox';

const CheckoutPayment = ({
  paymentOption,
  onPay,
  disabled,
  total,
  paymentKey,
  onFail,
  isDelivery,
  selectedDeliveryPayment,
  onSelectDeliveryOption,
  cardFormSubmitRef,
  allowCash,
  restaurant,
  requireConfirmAlcohol,
}) => {
  const { t } = useTranslation(['translation', 'delivery']);
  const { currencyCode } = useCurrency();

  const [cardFormVisible, setCardFormVisible] = useState(false);
  const [cardButtonLoading, setCardButtonLoading] = useState(false);
  const [conditionsAgreed, setConditionsAgreed] = useState(false);
  const [alcoholAgreed, setAlcoholAgreed] = useState(false);

  const needConfirmConditions = !conditionsAgreed && restaurant.requireConfirmConditions;
  const needConfirmAlcohol = !alcoholAgreed && requireConfirmAlcohol;

  if (!paymentOption || (!paymentOption.canPayBank && !paymentOption.canPayCard)) {
    if (!allowCash) {
      return (
        <div>
          <p className="font-primaryMedium text-primary text-base">
            {t('translation:checkout.paymentNotSetup')}
          </p>
        </div>
      );
    }
  }

  const renderDeliveryOption = (icon, title, subtitle, onClick, selected) => {
    return (
      <div
        role="button"
        onClick={onClick}
        className={`flex flex-row justify-between items-center
          border ${
            selected ? 'border-primary bg-successLight' : 'border-input'
          } px-5 py-3 rounded-md min-h-[4.375rem]`}
      >
        <div className="flex flex-row items-center">
          <div>{icon}</div>
          <div>
            <div className="font-primarySemiBold text-base text-primary">{title}</div>
            {!!subtitle && <div className="font-primaryRegular text-sm text-bland">{subtitle}</div>}
          </div>
        </div>
      </div>
    );
  };

  const getCacheButtonText = () => {
    if (
      [
        '6655f6f819551c534379b634',
        '66430116d8304dd333c986c8',
        '665f315c19551c5343844cbe',
        '64ef42bd69364cb8ac09a8ee',
      ].includes(restaurant.id)
    ) {
      return t('translation:buttons.cashMakeOrder');
    }

    return restaurant.cashPayAtBarText
      ? t('translation:buttons.cashPaymentAtBar')
      : t('translation:buttons.cashPayment');
  };

  return (
    <div>
      {!isDelivery && (
        <p className="font-primaryMedium text-primary text-base">
          {t('translation:checkout.choosePaymentMethod')}
        </p>
      )}
      {!isDelivery && (
        <p className="font-primaryMedium text-bland text-sm mt-1 mb-5">
          {t('translation:checkout.securePayment')}{' '}
          <MdLock className="inline -mt-[3px]" color="#0C7F48" />
        </p>
      )}
      {!isDelivery && !!requireConfirmAlcohol && (
        <div className="flex max-w-xs mx-auto">
          <CheckBox
            checked={alcoholAgreed}
            onClick={(checked) => setAlcoholAgreed(checked)}
            title={
              <span>
                {t('products.ageConfirmationTitle', {
                  ageNumber: restaurant.restrictionsAge || 18,
                })}{' '}
                {t('products.ageConfirmationDescription')}
              </span>
            }
            isSquare
            textSize="sm"
          />
        </div>
      )}
      {!isDelivery && !!restaurant.requireConfirmConditions && (
        <div className="flex max-w-xs mx-auto">
          <CheckBox
            checked={conditionsAgreed}
            onClick={(checked) => setConditionsAgreed(checked)}
            title={
              <Trans i18nKey="checkout.agreeTerms">
                By checking out you agree with our{' '}
                <a
                  className="underline"
                  target="_blank"
                  href="https://drive.google.com/file/d/1vYkyVHjaMx0e9qsToZh5S6-WBtcIXnyc/view"
                  rel="noreferrer"
                >
                  terms & conditions
                </a>{' '}
                and{' '}
                <a
                  className="underline"
                  target="_blank"
                  href="https://drive.google.com/file/d/1Et5cXjKQ6vaNWElDDnEZB4K3Rz4Ch8D8/view"
                  rel="noreferrer"
                >
                  privacy policy
                </a>
              </Trans>
            }
            isSquare
            textSize="sm"
          />
        </div>
      )}
      <div className={`${isDelivery ? '' : 'mt-8 max-w-xs mx-auto'} w-full`}>
        {paymentOption && paymentOption.canPayBank && (
          <>
            {isDelivery ? (
              renderDeliveryOption(
                <BiLinkExternal className="text-primary mr-5" size={26} />,
                t('translation:buttons.bankTransfer'),
                t('delivery:checkout.bankExamples'),
                () => {
                  onSelectDeliveryOption('Bank');
                  if (paymentOption.cardProvider === 'Stripe') {
                    setCardFormVisible(false);
                  }

                  if (paymentOption.cardProvider === 'Paynt') {
                    setCardFormVisible(false);
                  }

                  if (paymentOption.cardProvider === 'Zen' && !paymentOption.zenUseRedirect) {
                    setCardFormVisible(false);
                  }
                },
                selectedDeliveryPayment === 'Bank',
              )
            ) : (
              <MainButton
                icon={<MdOpenInNew className="inline-block mr-2 -mt-[2px]" />}
                text={t('translation:buttons.bankTransfer')}
                onClick={() =>
                  onPay({ paymentType: 'Bank', paymentAccountId: paymentOption.bankProviderId })
                }
                disabled={disabled || needConfirmConditions || needConfirmAlcohol}
              />
            )}

            <div className="mt-3" />
          </>
        )}
        {paymentOption && paymentOption.canPayCard && (
          <>
            {isDelivery ? (
              renderDeliveryOption(
                <BiLinkExternal className="text-primary mr-5" size={26} />,
                t('translation:buttons.cardPayment'),
                null,
                () => {
                  onSelectDeliveryOption('Card');
                  if (paymentOption.cardProvider === 'Stripe') {
                    setCardFormVisible(true);
                  }

                  if (paymentOption.cardProvider === 'Paynt') {
                    setCardFormVisible(true);
                  }

                  if (paymentOption.cardProvider === 'Zen' && !paymentOption.zenUseRedirect) {
                    setCardFormVisible(true);
                  }
                },
                selectedDeliveryPayment === 'Card',
              )
            ) : (
              <MainButton
                icon={<BsCreditCard2Back className="inline-block mr-2 -mt-[3px]" />}
                text={t('translation:buttons.cardPayment')}
                onClick={() => {
                  if (paymentOption.cardProvider === 'Stripe') {
                    setCardFormVisible((prev) => !prev);
                  } else if (paymentOption.cardProvider === 'Paynt') {
                    setCardFormVisible((prev) => !prev);
                  } else if (
                    paymentOption.cardProvider === 'Zen' &&
                    !paymentOption.zenUseRedirect
                  ) {
                    setCardFormVisible((prev) => !prev);
                  } else {
                    onPay({ paymentType: 'Card', paymentAccountId: paymentOption.cardProviderId });
                  }
                }}
                disabled={disabled || needConfirmConditions || needConfirmAlcohol}
                outline={cardFormVisible}
                loading={cardButtonLoading}
              />
            )}
            <div className="mt-3" />
          </>
        )}
        {paymentOption &&
          (paymentOption.cardProvider === 'Stripe' ||
            paymentOption.additionalWalletProvider === 'Stripe') && (
            <StripeFullPaymentContainer
              allowCards={paymentOption.stripeAllowCards}
              allowWallets={paymentOption.stripeAllowWallets}
              amount={total}
              cardFormVisible={cardFormVisible}
              currencyCode={currencyCode}
              onPay={({ paymentMethodId }) => {
                let stripePaymentAccountId;
                if (paymentOption.cardProvider === 'Stripe') {
                  stripePaymentAccountId = paymentOption.cardProviderId;
                } else if (paymentOption.additionalWalletProvider === 'Stripe') {
                  stripePaymentAccountId = paymentOption.additionalWalletProviderId;
                }

                onPay({
                  paymentType: 'Card',
                  paymentMethodId,
                  paymentAccountId: stripePaymentAccountId,
                });
              }}
              onFail={onFail}
              publicKey={paymentOption.stripePublicKey}
              isConnected={paymentOption.stripeIsConnected}
              connectedId={paymentOption.stripeConnectedId}
              paymentKey={paymentKey}
              disabled={disabled || needConfirmConditions || needConfirmAlcohol}
              cardFormSubmitRef={cardFormSubmitRef}
              isDelivery={isDelivery}
            />
          )}
        {paymentOption && paymentOption.cardProvider === 'Paynt' && (
          <PayntPaymentContainer
            amount={total}
            cardFormVisible={cardFormVisible}
            onPay={({ paymentTrackId }) => {
              onPay({
                paymentType: 'Card',
                dontDisable: true,
                paymentTrackId,
                paymentAccountId: paymentOption.cardProviderId,
              });
            }}
            paymentKey={paymentKey}
            disabled={disabled || needConfirmConditions || needConfirmAlcohol}
          />
        )}
        {paymentOption && paymentOption.cardProvider === 'Zen' && !paymentOption.zenUseRedirect && (
          <ZenPaymentContainer
            amount={total}
            cardFormVisible={cardFormVisible}
            onPay={({ paymentTrackId }) => {
              onPay({
                paymentType: 'Card',
                dontDisable: true,
                paymentTrackId,
                paymentAccountId: paymentOption.cardProviderId,
              });
            }}
            paymentKey={paymentKey}
            disabled={disabled || needConfirmConditions || needConfirmAlcohol}
            onSetButtonLoading={(val) => {
              setCardButtonLoading(val);

              if (!val) {
                setCardFormVisible(false);
              }
            }}
          />
        )}
        {allowCash && (
          <>
            <div className="mt-3" />
            <MainButton
              icon={<BsCash className="inline-block mr-2 -mt-[2px]" />}
              text={getCacheButtonText()}
              onClick={() => onPay({ paymentType: 'Cash' })}
              disabled={disabled || needConfirmConditions || needConfirmAlcohol}
            />
            <div className="mt-3" />
          </>
        )}
      </div>
      {!isDelivery && !restaurant.requireConfirmConditions && (
        <div className="flex justify-center">
          <button
            type="button"
            className="font-primaryMedium text-primary text-sm mt-2"
            onClick={() => {}}
          >
            <Trans i18nKey="checkout.agreeTerms">
              By checking out you agree with our{' '}
              <a
                className="underline"
                target="_blank"
                href="https://drive.google.com/file/d/1vYkyVHjaMx0e9qsToZh5S6-WBtcIXnyc/view"
                rel="noreferrer"
              >
                terms & conditions
              </a>{' '}
              and{' '}
              <a
                className="underline"
                target="_blank"
                href="https://drive.google.com/file/d/1Et5cXjKQ6vaNWElDDnEZB4K3Rz4Ch8D8/view"
                rel="noreferrer"
              >
                privacy policy
              </a>
            </Trans>
          </button>
        </div>
      )}
    </div>
  );
};

export default CheckoutPayment;
